
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";

import EmptyState from "../components/empty-state.vue";
import FilterTabs from "../components/filter-tabs.vue";
import { Icon } from "../components/icons";
import ListingCard from "../components/listing-card.vue";
import PaginationBar from "../components/pagination-bar.vue";
import { categories } from "../data/categories";
import {
  filterListings,
  hasFilter,
  paginate,
  parseQuery,
  resolveCategory,
  toQuery,
} from "../data/query";
import { QueryState } from "../data/types";
import { lastQuery, recentListings, rememberQuery } from "../store/preferences";
import common from "../styles/common.module.less";
import style from "./style.module.less";

const TYPE_TABS = [
  { label: "全部来源", value: "" },
  { label: "个人发布", value: "personal" },
  { label: "商家发布", value: "merchant" },
];

export default defineComponent({
  name: "XiyouPortal",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const state = reactive<QueryState>(
      parseQuery(route.query as Record<string, unknown>)
    );
    const keyword = ref(state.q);

    const categoryTabs = computed(() => [
      { label: "全部信息", value: "" },
      ...categories.map((item) => ({ label: item.name, value: item.slug })),
    ]);

    // 筛选状态以 URL query 为唯一真源：切换筛选条件时回到第 1 页
    const navigate = (patch: Partial<QueryState>) => {
      const next: QueryState = { ...state, ...patch };
      if (patch.page === undefined) next.page = 1;
      rememberQuery({ ...next, page: 1 });
      router.replace({ name: "XiyouPortal", query: toQuery(next) });
    };

    const applyQuery = (raw: Record<string, unknown>) => {
      const next = parseQuery(raw);
      state.category = next.category;
      state.type = next.type;
      state.q = next.q;
      state.page = next.page;
      keyword.value = next.q;
    };

    watch(
      () => route.query,
      (query) => applyQuery(query as Record<string, unknown>)
    );

    onMounted(() => {
      const raw = route.query as Record<string, unknown>;
      if (Object.keys(raw).length === 0) {
        const last = lastQuery();
        if (last.category || last.type || last.q) {
          navigate({ category: last.category, type: last.type, q: last.q });
          return;
        }
      }
      applyQuery(raw);
    });

    const filtered = computed(() => filterListings(state));
    const paged = computed(() => paginate(filtered.value, state.page));
    const recent = computed(() => recentListings());
    const heading = computed(() => {
      const category = resolveCategory(state.category);
      return category ? category.name : "全部信息";
    });

    const submitSearch = () => navigate({ q: keyword.value.trim() });

    const reset = () => {
      keyword.value = "";
      navigate({ category: "", type: "", q: "" });
    };

    return () => {
      const result = paged.value;
      return (
        <div class={common.shell}>
          <h1 class={common.pageTitle}>信息门户</h1>
          <p class={common.pageSubtitle}>全部分类信息一站浏览，实时更新</p>

          <div class={common.toolbar}>
            <form
              class={[common.searchBox, style.searchForm]}
              onSubmit={(event: Event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <input
                class={common.searchInput}
                value={keyword.value}
                placeholder="搜索信息 / 服务..."
                onInput={(event: Event) => {
                  keyword.value = (event.target as HTMLInputElement).value;
                }}
              />
              <button class={common.searchBtn} type="submit">
                <Icon name="search" />
                搜索
              </button>
            </form>
            <div class={style.toolbarActions}>
              {hasFilter(state) ? (
                <button class={common.btn} type="button" onClick={reset}>
                  清空筛选
                </button>
              ) : null}
              <RouterLink
                class={[common.btn, common.btnPrimary]}
                to={{ name: "XiyouPublish" }}
              >
                免费发布
              </RouterLink>
            </div>
          </div>

          <div class={style.filters}>
            <div class={style.filterRow}>
              <span class={style.filterLabel}>分类</span>
              <FilterTabs
                items={categoryTabs.value}
                modelValue={state.category}
                onChange={(value: string) => navigate({ category: value })}
              />
            </div>
            <div class={style.filterRow}>
              <span class={style.filterLabel}>来源</span>
              <FilterTabs
                items={TYPE_TABS}
                modelValue={state.type}
                onChange={(value: string) => navigate({ type: value })}
              />
            </div>
          </div>

          {recent.value.length ? (
            <section class={style.recent}>
              <div class={style.recentTitle}>
                <Icon name="clock" />
                最近浏览
              </div>
              <div class={common.chips}>
                {recent.value.map((item) => (
                  <RouterLink
                    key={item.id}
                    class={common.chip}
                    to={{
                      name: "XiyouDetail",
                      params: { id: String(item.id) },
                    }}
                  >
                    {item.title}
                  </RouterLink>
                ))}
              </div>
            </section>
          ) : null}

          <div class={style.resultHead}>
            <span class={common.count}>
              {heading.value} · 共 {result.total} 条
            </span>
            <span class={common.count}>
              第 {result.page} / {result.pageCount} 页
            </span>
          </div>

          {result.items.length ? (
            <div>
              <div class={common.grid}>
                {result.items.map((item) => (
                  <ListingCard key={item.id} listing={item} />
                ))}
              </div>
              {result.pageCount > 1 ? (
                <PaginationBar
                  page={result.page}
                  pageCount={result.pageCount}
                  onChange={(page: number) => navigate({ page })}
                />
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="没有找到符合条件的信息"
              desc="换个关键词，或清空筛选条件后重试。"
              icon="search"
            >
              <button
                class={[common.btn, common.btnPrimary]}
                type="button"
                onClick={reset}
              >
                清空筛选
              </button>
            </EmptyState>
          )}
        </div>
      );
    };
  },
});
